import { useCallback, useMemo } from 'react'

import { MultiSelect, Select } from 'govwell-ui'
import {
  MultiSelectModeProps,
  SelectBaseProps,
  SelectOption,
  SingleSelectModeProps,
} from 'govwell-ui/components/Select/types'
import isNil from 'lodash.isnil'
import { observer } from 'mobx-react-lite'
import { FormulaOutputUserType, UserDataFragment } from 'types/graphql'

import { useMyOrgActiveUsersOfType } from 'src/layouts/AppStateContextLayout/utils'
import { dedupeObjectsById, filterNil, formatUserName } from 'src/utils'

type BaseProps = Omit<SelectBaseProps<UserDataFragment>, 'getOptionKey' | 'options'> & {
  customDisplay?: (value: UserDataFragment) => React.ReactNode
  type: FormulaOutputUserType | null | undefined
}

type SingleSelectProps = BaseProps & SingleSelectModeProps<UserDataFragment>

type MultiSelectProps = BaseProps &
  MultiSelectModeProps<UserDataFragment> & {
    isSelectAllEnabled?: boolean
    shouldDisplayAggregatedSelectedOptions?: boolean
  }

type Props = SingleSelectProps | MultiSelectProps

const StaffUserSelect = (props: Props) => {
  const {
    customDisplay,
    errorMessage,
    filterOption,
    isClearable,
    isDisabled,
    isInvalid,
    isMultiSelect,
    isRequired,
    label,
    onBlur,
    onValueChange,
    placeholder,
    value,
    type,
    width,
  } = props
  const getOptionFromUser = useCallback(
    (user: UserDataFragment): SelectOption<UserDataFragment> => ({
      customDisplay,
      label: formatUserName(user),
      value: user,
    }),
    [customDisplay]
  )

  const users = useMyOrgActiveUsersOfType(type)
  const options: SelectOption<UserDataFragment>[] = useMemo(
    () =>
      dedupeObjectsById([...filterNil(Array.isArray(value) ? value : [value]), ...users])
        .map(getOptionFromUser)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [getOptionFromUser, users, value]
  )

  const selectedUuidSet = useMemo(() => {
    const selectedUsers = isNil(value) ? [] : Array.isArray(value) ? value : [value]
    return new Set(selectedUsers.map((u) => u.uuid))
  }, [value])
  const selectedOptions = useMemo(() => {
    return options.filter((o) => selectedUuidSet.has(o.value.uuid))
  }, [options, selectedUuidSet])
  const selectedOption = selectedOptions[0]

  const handleSelectedOptionsChange = useCallback(
    (newSelectedOptions: SelectOption<UserDataFragment>[] | undefined) => {
      if (isMultiSelect) {
        onValueChange(newSelectedOptions?.map((o) => o.value) ?? [])
      }
    },
    [isMultiSelect, onValueChange]
  )

  const handleSelectedOptionChange = useCallback(
    (newSelectedOption: SelectOption<UserDataFragment> | undefined) => {
      if (!isMultiSelect) {
        onValueChange(newSelectedOption?.value)
      }
    },
    [isMultiSelect, onValueChange]
  )

  const getOptionKey = useCallback((option: SelectOption<UserDataFragment>) => option.value.id, [])

  const commonProps: SelectBaseProps<UserDataFragment> = {
    ...(label ? { label } : { ariaLabel: 'User' }),
    errorMessage,
    filterOption,
    getOptionKey,
    isClearable,
    isDisabled,
    isInvalid,
    isRequired,
    onBlur,
    options,
    placeholder,
    width,
  }

  const aggregatedSelectedOptionsDisplayText = useMemo(() => {
    if (
      !selectedOptions?.length ||
      !isMultiSelect ||
      !props.shouldDisplayAggregatedSelectedOptions
    ) {
      return undefined
    }

    const userTerm = type === 'Inspector' ? 'inspector' : 'user'

    if (selectedOptions?.length === options.length) {
      return `All ${userTerm}s`
    }

    return `${selectedOptions.length} of ${options.length} ${userTerm}${options.length === 1 ? '' : 's'}`
  }, [isMultiSelect, options.length, props, selectedOptions.length, type])

  if (isMultiSelect) {
    return (
      <MultiSelect
        selectedOptions={selectedOptions}
        onSelectedOptionsChange={handleSelectedOptionsChange}
        selectedOptionsDisplayText={aggregatedSelectedOptionsDisplayText}
        isSelectAllEnabled={props.isSelectAllEnabled}
        {...commonProps}
      />
    )
  }

  return (
    <Select
      onSelectedOptionChange={handleSelectedOptionChange}
      selectedOption={selectedOption}
      {...commonProps}
    />
  )
}

export default observer(StaffUserSelect)
